import type { components } from "@/api-schema";
import { CompanyGroupIdentifierSystem } from "@/api-schema-enums";
import { normalizePositiveSafeIntegerIdentifier } from "@/common/helpers/normalize-numeric-identifier";
import { z } from "zod";

type CompanyGroup = Pick<components["schemas"]["CompanyGroup"], "companyGroupIdentifiers" | "uuid">;
export type CompanyGroupIdentifier = components["schemas"]["CompanyGroupIdentifier"];
export type CompanyGroupIdentifierDto = components["schemas"]["CompanyGroupIdentifierDto"];

const identifierInputSchema = z.string().trim();

export const companyGroupIdentifierFormSchema = z.object({
  paperless: identifierInputSchema.refine(
    (value) =>
      value === "" || normalizeCompanyGroupIdentifier(CompanyGroupIdentifierSystem.PAPERLESS, value) !== undefined,
    "masterdata:PaperlessWorkspaceIdMustBePositiveInteger",
  ),
});

export type CompanyGroupIdentifierFormValues = z.infer<typeof companyGroupIdentifierFormSchema>;

export const companyGroupIdentifierFormDefaultValues: CompanyGroupIdentifierFormValues = {
  paperless: "",
};

export function normalizeCompanyGroupIdentifier(
  type: CompanyGroupIdentifierSystem,
  identifier: string,
): string | undefined {
  if (type === CompanyGroupIdentifierSystem.PAPERLESS) {
    return normalizePositiveSafeIntegerIdentifier(identifier);
  }

  const value = identifier.trim();
  return value || undefined;
}

export function getCompanyGroupIdentifier(
  companyGroup: Pick<CompanyGroup, "companyGroupIdentifiers">,
  type: CompanyGroupIdentifierSystem,
): string | undefined {
  return companyGroup.companyGroupIdentifiers?.find((identifier) => identifier.type === type)?.identifier;
}

export function getCompanyGroupPaperlessWorkspaceId(
  companyGroup: Pick<CompanyGroup, "companyGroupIdentifiers">,
): string | undefined {
  return getCompanyGroupIdentifier(companyGroup, CompanyGroupIdentifierSystem.PAPERLESS);
}

export function getCompanyGroupIdentifierKey(
  type: CompanyGroupIdentifierSystem,
  identifier: string,
): string | undefined {
  const normalizedIdentifier = normalizeCompanyGroupIdentifier(type, identifier);
  return normalizedIdentifier === undefined ? undefined : `${type}\u0000${normalizedIdentifier}`;
}

export function isDuplicateCompanyGroupIdentifier(
  duplicateKeys: ReadonlySet<string>,
  type: CompanyGroupIdentifierSystem,
  identifier: string,
): boolean {
  const key = getCompanyGroupIdentifierKey(type, identifier);
  return key !== undefined && duplicateKeys.has(key);
}

export function toCompanyGroupIdentifierFormValues(
  companyGroupIdentifiers?: ReadonlyArray<Pick<CompanyGroupIdentifier, "type" | "identifier">>,
): CompanyGroupIdentifierFormValues {
  const getIdentifier = (type: CompanyGroupIdentifierSystem) =>
    companyGroupIdentifiers?.find((identifier) => identifier.type === type)?.identifier ?? "";

  return {
    paperless: getIdentifier(CompanyGroupIdentifierSystem.PAPERLESS),
  };
}

export function toCompanyGroupIdentifierDtos(values: CompanyGroupIdentifierFormValues): CompanyGroupIdentifierDto[] {
  const identifiers: CompanyGroupIdentifierDto[] = [];

  const appendIdentifier = (type: CompanyGroupIdentifierSystem, identifier: string) => {
    const normalizedIdentifier = normalizeCompanyGroupIdentifier(type, identifier);
    if (normalizedIdentifier !== undefined) identifiers.push({ type, identifier: normalizedIdentifier });
  };

  appendIdentifier(CompanyGroupIdentifierSystem.PAPERLESS, values.paperless);

  return identifiers;
}

export function getDuplicateCompanyGroupIdentifierKeys(
  companyGroups: ReadonlyArray<CompanyGroup> | undefined,
  values: CompanyGroupIdentifierFormValues,
  excludeCompanyGroupUuid?: string,
): Set<string> {
  if (!companyGroups) return new Set();

  const existingKeys = new Set(
    companyGroups
      .filter((companyGroup) => companyGroup.uuid !== excludeCompanyGroupUuid)
      .flatMap((companyGroup) => companyGroup.companyGroupIdentifiers ?? [])
      .map((identifier) => getCompanyGroupIdentifierKey(identifier.type, identifier.identifier))
      .filter((key): key is string => key !== undefined),
  );

  return new Set(
    toCompanyGroupIdentifierDtos(values)
      .map((identifier) => getCompanyGroupIdentifierKey(identifier.type, identifier.identifier))
      .filter((key): key is string => key !== undefined && existingKeys.has(key)),
  );
}
